import { useCallback, useEffect, useMemo, useState } from "react";
import { buildThemeClasses } from "../theme";
import { fetchLeads, type Lead } from "../services/leadsService";
import {
  LEAD_SOURCES,
  OTHER_SOURCE,
  resolveSource,
  type SourceDef,
} from "../services/leadSources";
import { aggregateByCampaign, aggregateByChannel } from "../services/leadAttributionStats";

// ===========================================================================
// Fuentes — LIVE section. The lead "sources" are not a hand-edited list: they
// are derived from the real leads in the CRM. Every lead carries a `source`
// (set by the landing forms / ad webhooks); here we resolve each to its
// canonical source and aggregate counts + conversion. The catalog of sources
// shown is fixed (the six channels we capture from), with an "Otro" row added
// only when leads arrive tagged with something unrecognized.
// ===========================================================================

interface SourceRow {
  def: SourceDef;
  total: number;
  nuevos: number;
  convertidos: number;
  conversion: number; // 0..100
}

function aggregate(leads: Lead[]): SourceRow[] {
  const counts = new Map<string, { total: number; nuevos: number; convertidos: number }>();
  const seenOther = { present: false };

  for (const lead of leads) {
    const def = resolveSource(lead.source);
    if (def.key === OTHER_SOURCE.key) seenOther.present = true;
    const c = counts.get(def.key) ?? { total: 0, nuevos: 0, convertidos: 0 };
    c.total += 1;
    if (lead.status === "new") c.nuevos += 1;
    if (lead.status === "converted") c.convertidos += 1;
    counts.set(def.key, c);
  }

  // Always show the six canonical sources; append "Otro" only if it has leads.
  const defs = [...LEAD_SOURCES];
  if (seenOther.present) defs.push(OTHER_SOURCE);

  return defs.map((def) => {
    const c = counts.get(def.key) ?? { total: 0, nuevos: 0, convertidos: 0 };
    return {
      def,
      total: c.total,
      nuevos: c.nuevos,
      convertidos: c.convertidos,
      conversion: c.total > 0 ? (c.convertidos / c.total) * 100 : 0,
    };
  });
}

export default function FuentesLive() {
  const tc = buildThemeClasses();

  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const page = await fetchLeads({ limit: 200 });
      setLeads(page.data);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err.response?.data?.message || err.message || "No se pudieron cargar las fuentes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => aggregate(leads), [leads]);
  // Atribución (#1521): por canal de captación y por campaña.
  const channelRows = useMemo(() => aggregateByChannel(leads), [leads]);
  const campaignRows = useMemo(() => aggregateByCampaign(leads), [leads]);
  const totalLeads = leads.length;
  const activas = rows.filter((r) => r.total > 0).length;
  // Tokens del rediseño de WAYAK (tema por tokens, no por isDarkMode).
  const rowClass = (i: number) => `text-content ${i % 2 === 0 ? "bg-surface" : "bg-card"}`;
  const fmtPct = (total: number, conversion: number) =>
    total > 0 ? `${conversion.toFixed(conversion % 1 === 0 ? 0 : 1)}%` : "—";

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
              <i className="ri-global-line text-xl" />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>Fuentes de lead</h1>
              <p className={`text-sm ${tc.textSecondary}`}>
                Orígenes de captación, en vivo desde los leads capturados
              </p>
            </div>
          </div>

          <button
            onClick={load}
            className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary transition-colors hover:bg-primary-400"
          >
            <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`} />
            <span className="whitespace-nowrap">Actualizar</span>
          </button>
        </div>

        {/* Summary cards */}
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <SummaryCard icon="ri-user-add-line" label="Leads totales" value={totalLeads} tc={tc} />
          <SummaryCard icon="ri-global-line" label="Fuentes activas" value={`${activas} / ${rows.length}`} tc={tc} />
          <SummaryCard
            icon="ri-line-chart-line"
            label="Conversión global"
            value={`${totalLeads > 0 ? Math.round((leads.filter((l) => l.status === "converted").length / totalLeads) * 100) : 0}%`}
            tc={tc}
          />
        </div>

        {/* Table */}
        <div className={`rounded-2xl border ${tc.cardBorder} ${tc.cardBg}`}>
          {loading ? (
            <div className={`p-10 text-center text-sm ${tc.textSecondary}`}>
              <i className="ri-loader-4-line mr-2 animate-spin" />
              Cargando fuentes...
            </div>
          ) : error ? (
            <div className="p-10 text-center">
              <i className="ri-error-warning-line mb-2 text-3xl text-red-500" />
              <p className="text-sm text-red-500">{error}</p>
              <button onClick={load} className="mt-3 rounded-lg bg-primary-500 px-4 py-2 text-sm text-on-primary hover:bg-primary-400">
                Reintentar
              </button>
            </div>
          ) : (
            <div className="w-full overflow-x-auto p-4">
              <table className="w-full overflow-hidden rounded-xl text-xs shadow-sm">
                <thead className="bg-primary-500 text-on-primary">
                  <tr>
                    <th className="p-3 text-left font-bold">Fuente</th>
                    <th className="p-3 text-left font-bold">Tipo</th>
                    <th className="p-3 text-center font-bold">Leads</th>
                    <th className="p-3 text-center font-bold">Nuevos</th>
                    <th className="p-3 text-center font-bold">Convertidos</th>
                    <th className="p-3 text-center font-bold">Conversión</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr
                      key={r.def.key}
                      className={`text-content ${i % 2 === 0 ? "bg-surface" : "bg-card"}`}
                    >
                      <td className="p-2">
                        <span className="flex items-center gap-2 font-medium">
                          <span className={`flex h-6 w-6 items-center justify-center rounded-md ${r.def.tone}`}>
                            <i className={r.def.icon} />
                          </span>
                          {r.def.label}
                        </span>
                      </td>
                      <td className="p-2">{r.def.tipo}</td>
                      <td className="p-2 text-center font-semibold">{r.total}</td>
                      <td className="p-2 text-center">{r.nuevos}</td>
                      <td className="p-2 text-center">{r.convertidos}</td>
                      <td className="p-2 text-center">
                        {r.total > 0 ? `${r.conversion.toFixed(r.conversion % 1 === 0 ? 0 : 1)}%` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className={`mt-3 text-xs ${tc.textSecondary}`}>
          <i className="ri-information-line mr-1" />
          Datos en vivo. Cada fuente agrupa los leads por su origen de captación
          (campo “source”). Las landings, los anuncios y los referidos etiquetan
          ese origen al momento de crear el lead.
        </p>

        {/* Atribución (#1521): por canal de captación. */}
        {!loading && !error && (
          <>
            <h2 className={`mb-2 mt-8 text-base font-bold ${tc.text}`}>Leads por canal de captación</h2>
            <p className={`mb-3 text-xs ${tc.textSecondary}`}>
              El canal dice por dónde llegó la persona (QR, folleto, anuncio, chat…), según la atribución
              que traen la landing, el registro y los webhooks de Meta. Los leads sin atribución se
              deducen de su fuente.
            </p>
            <div className={`rounded-2xl border ${tc.cardBorder} ${tc.cardBg}`} data-testid="fuentes-por-canal">
              <div className="w-full overflow-x-auto p-4">
                <table className="w-full overflow-hidden rounded-xl text-xs shadow-sm">
                  <thead className="bg-primary-500 text-white">
                    <tr>
                      <th className="p-3 text-left font-bold">Canal</th>
                      <th className="p-3 text-center font-bold">Leads</th>
                      <th className="p-3 text-center font-bold">Nuevos</th>
                      <th className="p-3 text-center font-bold">Convertidos</th>
                      <th className="p-3 text-center font-bold">Conversión</th>
                    </tr>
                  </thead>
                  <tbody>
                    {channelRows.map((r, i) => (
                      <tr key={r.def.key} className={rowClass(i)}>
                        <td className="p-2">
                          <span className="flex items-center gap-2 font-medium">
                            <span className={`flex h-6 w-6 items-center justify-center rounded-md ${r.def.tone}`}>
                              <i className={r.def.icon} />
                            </span>
                            {r.def.label}
                          </span>
                        </td>
                        <td className="p-2 text-center font-semibold">{r.total}</td>
                        <td className="p-2 text-center">{r.nuevos}</td>
                        <td className="p-2 text-center">{r.convertidos}</td>
                        <td className="p-2 text-center">{fmtPct(r.total, r.conversion)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Atribución (#1521): por campaña (utm_campaign). */}
            <h2 className={`mb-2 mt-8 text-base font-bold ${tc.text}`}>Leads por campaña</h2>
            <p className={`mb-3 text-xs ${tc.textSecondary}`}>
              Solo los leads que llegaron con una campaña (utm_campaign) en su enlace, código QR o anuncio.
            </p>
            <div className={`rounded-2xl border ${tc.cardBorder} ${tc.cardBg}`} data-testid="fuentes-por-campana">
              {campaignRows.length === 0 ? (
                <div className={`p-8 text-center text-sm ${tc.textSecondary}`}>
                  <i className="ri-megaphone-line mb-2 block text-3xl" />
                  Todavía no hay leads con campaña. Genera enlaces con utm_campaign y aparecerán aquí.
                </div>
              ) : (
                <div className="w-full overflow-x-auto p-4">
                  <table className="w-full overflow-hidden rounded-xl text-xs shadow-sm">
                    <thead className="bg-primary-500 text-white">
                      <tr>
                        <th className="p-3 text-left font-bold">Campaña</th>
                        <th className="p-3 text-left font-bold">Canales</th>
                        <th className="p-3 text-center font-bold">Leads</th>
                        <th className="p-3 text-center font-bold">Nuevos</th>
                        <th className="p-3 text-center font-bold">Convertidos</th>
                        <th className="p-3 text-center font-bold">Conversión</th>
                      </tr>
                    </thead>
                    <tbody>
                      {campaignRows.map((r, i) => (
                        <tr key={r.campaign} className={rowClass(i)}>
                          <td className="p-2 font-medium">{r.campaign}</td>
                          <td className="p-2">{r.channels.join(", ")}</td>
                          <td className="p-2 text-center font-semibold">{r.total}</td>
                          <td className="p-2 text-center">{r.nuevos}</td>
                          <td className="p-2 text-center">{r.convertidos}</td>
                          <td className="p-2 text-center">{fmtPct(r.total, r.conversion)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SummaryCard({
  icon,
  label,
  value,
  tc,
}: {
  icon: string;
  label: string;
  value: string | number;
  tc: ReturnType<typeof buildThemeClasses>;
}) {
  return (
    <div className={`flex items-center gap-3 rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
        <i className={`${icon} text-lg`} />
      </span>
      <div>
        <p className={`text-xs ${tc.textSecondary}`}>{label}</p>
        <p className={`text-lg font-bold ${tc.text}`}>{value}</p>
      </div>
    </div>
  );
}
